import React, { useState, useEffect } from 'react';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import './PhoneInput.css';

// Country code to country mapping
const getCountryFromCallingCode = (callingCode) => {
  const countries = getCountries();
  return countries.find(country => {
    try {
      return getCountryCallingCode(country) === callingCode;
    } catch {
      return false;
    }
  });
};

// Custom phone input with country code text input
function PhoneCodeInput({ value, onChange, placeholder, error, disabled, className }) {
  const [phoneCode, setPhoneCode] = useState('+1');
  const [nationalNumber, setNationalNumber] = useState('');
  const [country, setCountry] = useState('US');

  // Parse existing value when component mounts or value changes
  useEffect(() => {
    if (value && value.startsWith('+')) {
      // Try to parse the phone number
      for (let i = 1; i <= 4; i++) {
        const testCode = value.substring(1, 1 + i);
        const testCountry = getCountryFromCallingCode(testCode);
        if (testCountry) {
          setPhoneCode(`+${testCode}`);
          setNationalNumber(value.substring(1 + i));
          setCountry(testCountry);
          break;
        }
      }
    } else if (!value) {
      setPhoneCode('+1');
      setNationalNumber('');
      setCountry('US');
    }
  }, [value]);

  const handlePhoneCodeChange = (e) => {
    let input = e.target.value;
    
    // Ensure it starts with +
    if (input && !input.startsWith('+')) {
      input = '+' + input;
    }
    
    // Only allow digits after +
    input = input.replace(/[^+\d]/g, '');
    
    setPhoneCode(input);
    
    // Try to find matching country
    if (input.length > 1) {
      const code = input.substring(1);
      const matchedCountry = getCountryFromCallingCode(code);
      setCountry(matchedCountry);
    } else {
      setCountry(null);
    }

    // Update the full phone number
    updateFullNumber(input, nationalNumber);
  };

  const handleNationalNumberChange = (e) => {
    const input = e.target.value.replace(/[^\d\s\-()]/g, ''); // Allow common phone formatting chars
    setNationalNumber(input);
    updateFullNumber(phoneCode, input);
  };

  const updateFullNumber = (code, national) => {
    const fullNumber = code && national ? `${code}${national.replace(/[^\d]/g, '')}` : '';
    onChange(fullNumber);
  };

  return (
    <div className={`phone-code-input-wrapper ${className} ${error ? 'phone-input-error' : ''}`}>
      <div className="phone-input-container">
        {/* Country Code Input */}
        <div className="phone-code-section">
          <input
            type="text"
            value={phoneCode}
            onChange={handlePhoneCodeChange}
            placeholder="+1"
            className="phone-code-input"
            disabled={disabled}
            maxLength={5}
          />
          {country && (
            <div className="flag-container">
              <img 
                src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`}
                alt={`${country} flag`}
                className="flag-icon"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        {/* National Number Input */}
        <div className="phone-number-section">
          <input
            type="tel"
            value={nationalNumber}
            onChange={handleNationalNumberChange}
            placeholder={placeholder || "Phone Number *"}
            className="phone-number-input"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

export default function PhoneInputComponent({ 
  value, 
  onChange, 
  placeholder = "Phone Number *",
  className = "",
  error = false,
  disabled = false 
}) {
  return (
    <PhoneCodeInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      error={error}
      disabled={disabled}
    />
  );
}